/**
 * Account Daily Snapshot Service
 * Frontend client for /api/v2/snapshots
 */

import { auth } from '../sp-firebase/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export interface SnapshotPayload {
  version: number;
  account_formulas: any[];
  account_settings: Record<string, any> | null;
  notification_rules: any[];
  hedge_allocation_templates: any[];
  material_mappings: any[];
  demand_positions: any[];
  supply_positions: any[];
  hedge_positions: any[];
}

export interface AccountSnapshot {
  id: number;
  accountId: number;
  snapshotDate: string;
  dataHash: string;
  data: SnapshotPayload | null;
  previousSnapshotId: number | null;
  createdAt: string;
}

export interface SnapshotListItem {
  id: number;
  snapshotDate: string;
  createdAt: string;
  dataHash: string;
  deduped: boolean;
  sizeBytes: number;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export async function listSnapshots(
  accountId: number,
  opts?: { from?: string; to?: string; limit?: number },
): Promise<SnapshotListItem[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  if (opts?.limit) params.set('limit', String(opts.limit));

  const res = await fetch(`${API_URL}/api/v2/snapshots?${params.toString()}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to list snapshots (${res.status})`);
  }
  const data = await res.json();
  return data.snapshots || [];
}

export async function getSnapshot(
  accountId: number,
  date: string | 'latest',
): Promise<AccountSnapshot> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));

  const res = await fetch(`${API_URL}/api/v2/snapshots/${date}?${params.toString()}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch snapshot (${res.status})`);
  }
  const data = await res.json();
  return data.snapshot;
}

export async function getSnapshotById(
  accountId: number,
  snapshotId: number,
): Promise<AccountSnapshot> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));

  const res = await fetch(
    `${API_URL}/api/v2/snapshots/by-id/${snapshotId}?${params.toString()}`,
    { headers },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch snapshot (${res.status})`);
  }
  const data = await res.json();
  return data.snapshot;
}

export async function captureSnapshot(accountId: number): Promise<{
  snapshotId: number;
  accountId: number;
  snapshotDate: string;
  deduped: boolean;
  dataHash: string;
}> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  params.set('accountId', String(accountId));

  const res = await fetch(`${API_URL}/api/v2/snapshots/capture?${params.toString()}`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to capture snapshot (${res.status})`);
  }
  return res.json();
}
